import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { ISchemaObject, IOperation, Item, ItemPath } from '@augloop/types-core';
import { ISerializedNode } from '@augloop/types-debug';

/**
 * The current version of the message protocol. This is incremented when there are significant
 * changes to the flow of messages in a session, or breaking changes to individual message
 * schemas occur, but typically not when individual message schemas are updated in backward and
 * forward compatible ways.
 */
export const currentProtocolVersion: number = 2;

/**
 * Base interface for all messages.
 */
export interface IMessage extends ISchemaObject {
    /**
     * Identifier to allow matching the response to this message. Should be unique among messages
     * originated by a given sender. Typically used for messages sent via WebSocket; optional for
     * messages sent via HTTPS since it has implicit request/response matching. If not supplied
     * on a WebSocket connection, no response will be returned.
     */
    messageId?: string;

    /**
     * CorrelationVector for logging correlation.
     */
    cv?: string;
}

/**
 * Base interface for all responses. Each message results in a response to the sender to acknowledge
 * receipt of the message and supply any message-specific response information.
 */
export interface IResponse extends ISchemaObject {
    /**
     * Identifies the message for which this is the response.
     */
    messageId?: string;
}

/**
 * Base interface for error responses.
 */
export interface IErrorResponse extends IResponse {
    /**
     * Message-dependent error description; not set if the message was handled successfully.
     */
    readonly error: string;
}

/**
 * Specialization of IErrorResponse that indicates a response wasn't received in a timely manner.
 * Every message sent will eventually fail with this error if a response wasn't received and a more
 * specific failure reason isn't known.
 */
export interface ITimeoutErrorResponse extends IErrorResponse { }

/**
 * The following types define the Augmentation Loop message protocol for stateful sessions.
 *
 * The primary communication channel is a full-duplex connection over WebSocket. Some messages,
 * such as SyncMessage, are sent in both directions. Messages from the client to the server can
 * be sent on the primary channel or, alternatively, sent via HTTPS POST to an endpoint URL
 * supplied by the server as part of session initiation.
 */

/**
 * Initiates a session. Must be the first message sent by the client after connecting to the service.
 */
export interface ISessionInitMessage extends IMessage {
    /**
     * The version of the session message protocol used by the client.
     */
    readonly protocolVersion: number;

    /**
     * Metadata describing the client. Note that the sessionId it contains is for logging
     * purposes only, and is not used to identify the session state (that's what the session
     * key is used for).
     */
    readonly clientMetadata: IClientMetadata;

    /**
     * Optional session key to continue a session after network disconnect. If the session key is
     * unknown, creates an empty session, and a different sessionKey will be set in the response.
     * In that case, client must re-populate the session data model, re-activate workflows, etc.
     * just like for a new session.
     */
    readonly sessionKey?: string;

    /**
     * Represents data used for AL internal routing of messages.
     * This is needed for requests to AL which comes to session HTTPS endpoint.
     * This value should be sent in request's header with header name "X-Origin".
     */
    readonly origin?: string;

    /**
     * Optional token provided by the client in order to be able to access workflows requiring
     * authentication. In case authentication is not required for annotations the user wants to
     * receive, the token does not need to be included as part of the message.
     */
    readonly authToken?: string;

    /**
     * Optional configurations for custom session extensions. The types of the config objects
     * determine the custom session extensions that will be activated for this session.
     */
    readonly extensionConfigs?: ISchemaObject[];

    /**
     * Flag informing the session to return types to the AL Runtime, that could
     * cause a workflow to execute. Types are returned with SessionInitResponse.
     * The AL runtime uses this to decide if it should send signals to the server or just local workflows.
     */
    readonly returnWorkflowInputTypes?: boolean;

    /**
     * Turns on debug log collection so that a session inspector can later connect to this session and
     * receive all logs generated since the start of the session (see ISessionInspectorConnectMessage).
     */
    readonly enableSessionInspectorLogs?: boolean;
}

/**
 * Acknowledges the initiation of a session and provides information the client will need.
 */
export interface ISessionInitResponse extends IResponse {
    /**
     * A key that uniquely identifies this session.
     */
    readonly sessionKey: string;

    /**
     * HTTPS endpoint to which messages for sessions can be POSTed, equivalently to the client
     * sending the message via the primary communication channel. Typically used by browser clients
     * that have a service backend from which messages can also be sent. To POST messages to this
     * session, form the URL as follows: <sessionUrlBase>/<sessionKey>.
     * Note also that sending request on session endpoint requires
     * 1. X-Origin header which value should be populated by below origin string.
     * 2. Authorization header with string value "Bearer <JWT user token>"
     */
    readonly sessionUrlBase: string;

    /**
     * Represents data used for AL internal routing of messages.
     * This is needed for requests to AL which comes to session HTTPS endpoint.
     * This value should be sent in request's header with header name "X-Origin".
     */
    readonly origin: string;

    /**
     * WSS url to specific slice of AL deployment. Used for reconnecting session via WebSocket on same slice.
     */
    sliceUrl?: string;

    /**
     * Signals client to drop current WebSocket connection even if it works fine and reconnect again at global AL url.
     */
    forceReconnect?: boolean;

    /**
     * Expiration time for the token passed as part of the SessionInitMessage expressed in seconds since 00:00 UTC 1 Jan 1970
     */
    tokenExpirationTime?: number;

    /**
     * Number of seconds in which the token provided as part of the response will expire
     */
    tokenExpirationSeconds?: number;

    /**
     * Token assigned to the user in case provided auth token was undefined or invalid
     */
    anonymousToken?: string;

    /**
     * List of input types that could cause a workflow to execute.
     */
    workflowInputTypes?: string[];
}

/**
 * Ends a session, indicating that all resources associated with it can be released.
 *
 * Can be initiated by either the client or the service. Unlike other messages, this message
 * does not require a response; the recipient should close the connection.
 */
export interface ISessionCloseMessage extends IMessage {
    /**
     * If this message is initiated by the service, indicates that the client is allowed to try
     * to reconnect and re-establish the session. Otherwise, the client should not communicate
     * with the service for the remainder of its own session lifetime.
     */
    readonly reconnectAllowed?: boolean;

    /**
     * SchemaObject containing the reason for session close. The type of the reason varies based
     * on the scenario, e.g., specific to the Excel session extension if it triggers session close.
     */
    readonly reason?: ISchemaObject;
}

/**
 * Requests a dump of the current cache for a given session. Note: The client is responsible for sending it
 * to the 'right' URL base. This can be inferred from the sessionUrlBase sent back via the SessionInitResponse
 */
export interface ICacheDumpRequestMessage extends IMessage {
}

export interface ICacheDumpRequestResponse extends IResponse {
    /**
     * The serialized cache, if the dump request was successful
     */
    readonly serializedNodes: ISerializedNode[];
}

/**
 * Activates workflow(s) based on a desired annotation type. If the annotation
 * type has not already been activated, all workflows that are capable of producing
 * the annotation type will be executed against the current state of the session
 * data model. Annotations will be sent to the client via AnnotationResultMessages
 * while there is at least one activation for a given annotation type.
 */
export interface IAnnotationActivationMessage extends IMessage {
    /**
     * The ISchemaObject type of the desired annotation.
     */
    readonly annotationType: string;

    /**
     * A unique string to identify this instance on activation & release.
     */
    readonly token: string;

    /**
     * DEPRECATED (AnnotationResultMessages are no longer tied to any specific activation)
     * Bug 4026912: Remove support for legacy annotation activation behavior
     */
    readonly batchId?: string;

    /**
     * Optional configuration for this annotation type. This will be merged in an
     * annotation-dependent way if there are multiple activations of the same type.
     */
    readonly config?: ISchemaObject;

    /**
     * Indicates the server should ignore sending existing annotations of this type to the client.
     */
    readonly ignoreExistingAnnotations?: boolean;

    /**
     * Optional flag to receive updates for this annotation type. Setting this flag will cause
     * AnnotationResultStateMessages to appear for long-running workflows for this annotation type.
     */
    readonly sendStateUpdates?: boolean;
}

/**
 * DEPRECATED (AnnotationActivationMessages are now ack'd with generic Response/ErrorResponse)
 * Bug 4026912: Remove support for legacy annotation activation behavior
 */
export interface IAnnotationActivationResponse extends IResponse {
    /**
     * DEPRECATED (tokens are always client-supplied now)
     * Bug 4026912: Remove support for legacy annotation activation behavior
     */
    readonly token: string;
}

export enum AnnotationResultState {
    /**
     * No workflows are currently running and no annotation results will be produced until
     * additional SyncMessages are received.
     */
    Idle = 0,

    /**
     * Workflows are currently running and/or the client has just activated the annotation type;
     * more results may arrive. This is the default state after the first activation of a given
     * annotation type.
     */
    Pending = 1
}

export interface AnnotationResultStateUpdate {
    /**
     * The annotation type whose result state has changed.
     */
    readonly annotationType: string;

    /**
     * The new result state.
     */
    readonly state: AnnotationResultState;
}

/**
 * Notifies the client of major annotation result state changes. To avoid excessive chattiness,
 * this is not sent for every possible state change; workflows that execute and return to idle
 * quickly may not trigger this notification.
 *
 * Not yet implemented (User Story 4026838: Implement client/server support for annotation result state messages)
 */
export interface IAnnotationResultStateMessage extends IMessage {
    readonly updates: AnnotationResultStateUpdate[];
}

/**
 * Requests the release of an annotation that is no longer needed. Annotation results
 * will continue to be returned until all activations for a given annotation type
 * have been released.
 */
export interface IAnnotationReleaseMessage extends IMessage {
    /**
     * Token supplied in an earlier AnnotationActivationMessage.
     */
    readonly token: string;
}

/**
 * Acknowledges an annotation activation release.
 */
export interface IAnnotationReleaseResponse extends IResponse {
    /**
     * If true, indicates that this was the last release of the annotation type
     * and the client should clean up its internal data model (if a subsequent
     * activation occurs, the service will assume the client has no state with
     * respect to this annotation type and will re-send existing annotations).
     */
    readonly lastRelease: boolean;
}

/**
 * Synchronizes the session data model from client to service, or vice versa:
 * The client sends these to the service to update the core session data model,
 * and the service sends these to the client to add/update/delete annotations in
 * the session data model.
 */
export interface ISyncMessage extends IMessage {
    /**
     * The sequence number of this sync message. Sequential sync messages from a
     * given sender (client or service) must have consecutive sequence numbers,
     * starting with 1, and they must be applied in-order. Since sync messages
     * from the client can be sent via the primary (WebSocket) or secondary (HTTPS
     * endpoint) channel, they may arrive out-of-order and the service should buffer
     * non-consecutive messages for some period of time and wait for additional
     * messages to fill the gap.
     */
    readonly seq?: number;

    /**
     * The list of operations to apply to the model. Operations must be applied
     * in-order for consistency. May be empty to indicate that processing has
     * completed and no output was generated.
     */
    readonly ops: IOperation[];

    /**
     * An identifier that allows joining together multiple sequential (not
     * necessarily consecutive) sync messages that are logically one unit but
     * that have been split into multiple messages to avoid exceeding single
     * message size limits. Sync messages in a group share the same sequence
     * number.
     */
    readonly batchId?: string; // Old name (DEPRECATED)
    groupId?: string; // New name (make readonly when batchId is deprecated)

    /**
     * True if groupId is set and this is the last message in the group.
     */
    readonly batchComplete?: boolean; // Old name (DEPRECATED)
    groupComplete?: boolean; // New name (make readonly when batchComplete is deprecated)

    /**
     * If this is the last message in a group (i.e. groupComplete is true),
     * indicates the total number of messages in the group. This allows the
     * recipient to wait for the receipt of messages delivered out of order.
     */
    readonly batchSize?: number; // Old name (DEPRECATED)
    groupSize?: number; // New name (make readonly when batchSize is deprecated)
}

/**
 * Synchronizes the session data model from client to service, or vice versa.
 * Condensed into a micro format allowing single operations to be transported more efficiently.
 */
export interface IMicroSyncMessage extends IMessage {
    /**
     * The item to apply to the model.
     */
    readonly item: Item;

    /**
     * Sequence number (message is applied on receipt if omitted).
     * Not yet implemented
     */
    readonly seq?: number;

    /**
     * One of 'Add', 'Update', 'Delete' (defaults to 'Add' if omitted).
     * Not yet implemented
     */
    readonly opType?: string;

    /* Defaults to ['session'] if omitted */
    readonly parentPath?: ItemPath;
}

/**
 * Acknowledges receipt of a sync message.
 */
export interface ISyncResponse extends IResponse {
    /**
     * A requested minimum interval between sync messages, for throttling purposes.
     */
    readonly minimumSyncIntervalMs?: number;
}

/**
 * Note: At the moment used only for testing purposes.
 * Initiates session deletion in Mastermind without deleting it in persistable storages (Redis - warm cache)
 */
export interface ISessionDeleteMessage extends IMessage { }

/**
 * Specialization of ISyncMessage specifically for annotation results flowing
 * back from the service to the client.
 */
export interface IAnnotationResultsMessage extends ISyncMessage {
    /**
     * The previously activated annotation type that the operations in
     * this sync are associated with.
     */
    readonly annotationType: string;
}

/**
 * Provides additional token from client, for proper execution of workflows requiring additional identity claims
 */
export interface ITokenProvisionMessage extends IMessage {
    /**
     * Token provided by the client in order to be able to access authenticated workflows.
     */
    readonly authToken?: string;
}

/**
 * Acknowledges receipt of a token provision message.
 */
export interface ITokenProvisionResponse extends IResponse {
    /**
     * Expiration time for the token passed as part of the ITokenProvisionMessage, expressed in seconds since 00:00 UTC 1 Jan 1970. Undefined in case token was invalid in any way.
     */
    readonly tokenExpirationTime?: number;

    /**
     * Number of seconds in which the token provided as part of the response will expire
     */
    readonly tokenExpirationSeconds?: number;
}
